import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { CalendarDays, TrendingUp, Package, AlertTriangle, Sparkles, Plus, Trash2, ChevronLeft, ChevronRight, BarChart3, Loader2, ClipboardList, ArrowUpDown, FileDown, CheckSquare } from 'lucide-react';
import { planningAPI, productAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';

const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(mo, 10) - 1]} ${y}`;
};

const PlanningModule = () => {
  const [activeTab, setActiveTab] = useState('forecast');
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  return (
    <div className="p-6 space-y-4" data-testid="planning-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="planning-title">Production Planning</h1>
          <p className="text-sm text-gray-500 mt-0.5">Forecast demand, plan production, and manage material requirements</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="forecast" data-testid="tab-forecast" className="gap-1.5 text-xs">
            <TrendingUp className="w-3.5 h-3.5" /> Sales Forecast
          </TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan" className="gap-1.5 text-xs">
            <CalendarDays className="w-3.5 h-3.5" /> Production Plan
          </TabsTrigger>
          <TabsTrigger value="shortfall" data-testid="tab-shortfall" className="gap-1.5 text-xs">
            <AlertTriangle className="w-3.5 h-3.5" /> OC Shortfall
          </TabsTrigger>
          <TabsTrigger value="mrp" data-testid="tab-mrp" className="gap-1.5 text-xs">
            <Package className="w-3.5 h-3.5" /> MRP
          </TabsTrigger>
          <TabsTrigger value="indent" data-testid="tab-indent" className="gap-1.5 text-xs">
            <ClipboardList className="w-3.5 h-3.5" /> Indent Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="forecast"><ForecastTab user={user} /></TabsContent>
        <TabsContent value="plan"><ProductionPlanTab /></TabsContent>
        <TabsContent value="shortfall"><ShortfallTab /></TabsContent>
        <TabsContent value="mrp"><MRPTab /></TabsContent>
        <TabsContent value="indent"><IndentSummaryTab /></TabsContent>
      </Tabs>
    </div>
  );
};

// ============== FORECAST TAB ==============
const ForecastTab = ({ user }) => {
  const [forecasts, setForecasts] = useState([]);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [products, setProducts] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAI, setShowAI] = useState(false);
  const [form, setForm] = useState({ product_name: '', fg_item_id: '', quantity: '', unit: 'PCS', notes: '', source: 'Manual' });

  const fetchForecasts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await planningAPI.listForecasts({ month });
      setForecasts(Array.isArray(data) ? data : data.items || []);
    } catch { toast.error('Failed to load forecasts'); }
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchForecasts(); }, [fetchForecasts]);
  useEffect(() => {
    productAPI.getAll({ limit: 500 }).then(d => setProducts(Array.isArray(d) ? d : d.products || [])).catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!form.product_name || !form.quantity) { toast.error('Product name and quantity are required'); return; }
    try {
      await planningAPI.createForecast({ ...form, quantity: parseFloat(form.quantity), month });
      toast.success('Forecast added');
      setShowAdd(false);
      setForm({ product_name: '', fg_item_id: '', quantity: '', unit: 'PCS', notes: '', source: 'Manual' });
      fetchForecasts();
    } catch { toast.error('Failed to add forecast'); }
  };

  const handleDelete = async (id) => {
    try { await planningAPI.deleteForecast(id); toast.success('Deleted'); fetchForecasts(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleAI = async () => {
    setAiLoading(true);
    try {
      const res = await planningAPI.getAISuggestions(month);
      setAiSuggestions(res.suggestions || []);
      setShowAI(true);
      if (!res.suggestions?.length) toast.info('No AI suggestions available for this period');
    } catch { toast.error('AI suggestion failed'); }
    setAiLoading(false);
  };

  const acceptSuggestion = async (s) => {
    try {
      await planningAPI.createForecast({
        product_name: s.product_name, fg_item_id: s.fg_item_id || '', quantity: s.quantity,
        unit: s.unit || 'PCS', notes: s.reasoning || 'AI Suggested', source: 'AI Suggestion', month,
      });
      toast.success(`Added forecast for ${s.product_name}`);
      setAiSuggestions(prev => prev.filter(x => x !== s));
      fetchForecasts();
    } catch { toast.error('Failed to accept'); }
  };

  const acceptAllSuggestions = async () => {
    let count = 0;
    for (const s of aiSuggestions) {
      try {
        await planningAPI.createForecast({
          product_name: s.product_name, fg_item_id: s.fg_item_id || '', quantity: s.quantity,
          unit: s.unit || 'PCS', notes: s.reasoning || 'AI Suggested', source: 'AI Suggestion', month,
        });
        count++;
      } catch { /* skip failed */ }
    }
    toast.success(`Added ${count} forecasts from AI suggestions`);
    setAiSuggestions([]);
    setShowAI(false);
    fetchForecasts();
  };

  const selectProduct = (pid) => {
    const p = products.find(x => x.id === pid);
    if (p) setForm(f => ({ ...f, product_name: p.name, fg_item_id: p.item_code || p.id }));
  };

  const totalQty = forecasts.reduce((s, f) => s + (f.quantity || 0), 0);

  return (
    <div className="space-y-4 mt-2" data-testid="forecast-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-forecast-btn" className="gap-1.5 bg-[#24549c] hover:bg-[#1a3f7a]">
          <Plus className="w-3.5 h-3.5" /> Add Forecast
        </Button>
        <Button size="sm" variant="outline" onClick={handleAI} disabled={aiLoading} data-testid="ai-suggest-btn" className="gap-1.5">
          {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          AI Suggest
        </Button>
        <div className="ml-auto flex gap-3 text-xs text-gray-500">
          <span>Items: <strong className="text-gray-900">{forecasts.length}</strong></span>
          <span>Total Qty: <strong className="text-gray-900">{totalQty.toLocaleString()}</strong></span>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : forecasts.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No forecasts for {formatMonth(month)}. Add one or use AI suggestions.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>Product</TableHead>
                  <TableHead>FG Item ID</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {forecasts.map(f => (
                  <TableRow key={f.id} data-testid={`forecast-row-${f.id}`} className="text-xs">
                    <TableCell className="font-medium">{f.product_name}</TableCell>
                    <TableCell className="text-gray-500">{f.fg_item_id || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">{f.quantity.toLocaleString()}</TableCell>
                    <TableCell>{f.unit}</TableCell>
                    <TableCell>
                      <Badge variant={f.source === 'AI Suggestion' ? 'default' : 'secondary'} className="text-[10px]">{f.source}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={f.status === 'Approved' ? 'default' : 'outline'} className="text-[10px]">{f.status}</Badge>
                    </TableCell>
                    <TableCell className="text-gray-400 max-w-[140px] truncate">{f.notes || '-'}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(f.id)} data-testid={`delete-forecast-${f.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-forecast-dialog">
          <DialogHeader><DialogTitle>Add Sales Forecast — {formatMonth(month)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Product</label>
              <Select onValueChange={selectProduct}>
                <SelectTrigger data-testid="forecast-product-select"><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="mt-2" placeholder="Or type product name" value={form.product_name} onChange={e => setForm(f => ({...f, product_name: e.target.value}))} data-testid="forecast-product-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Quantity</label>
                <Input type="number" value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))} data-testid="forecast-quantity" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Unit</label>
                <Select value={form.unit} onValueChange={v => setForm(f => ({...f, unit: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['PCS','KG','MTR','LTR','SET','NOS','SQM','RMT','TON'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">FG Item ID</label>
                <Input value={form.fg_item_id} onChange={e => setForm(f => ({...f, fg_item_id: e.target.value}))} data-testid="forecast-fg-id" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} data-testid="forecast-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="save-forecast-btn" className="bg-[#24549c] hover:bg-[#1a3f7a]">Save Forecast</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Dialog */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="max-w-2xl" data-testid="ai-suggestions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" /> AI Forecast Suggestions — {formatMonth(month)}
            </DialogTitle>
          </DialogHeader>
          {aiSuggestions.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">No suggestions available</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{aiSuggestions.length} suggestions</span>
                <Button size="sm" onClick={acceptAllSuggestions} data-testid="accept-all-suggestions" className="bg-emerald-600 hover:bg-emerald-700 text-xs gap-1.5">
                  <Sparkles className="w-3 h-3" /> Accept All ({aiSuggestions.length})
                </Button>
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {aiSuggestions.map((s, i) => (
                  <div key={i} className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-gray-50" data-testid={`ai-suggestion-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{s.product_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.reasoning}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-600">
                        <span>Qty: <strong>{s.quantity}</strong></span>
                        <span>Unit: {s.unit || 'PCS'}</span>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => acceptSuggestion(s)} data-testid={`accept-suggestion-${i}`} className="bg-emerald-600 hover:bg-emerald-700 text-xs">Accept</Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== PRODUCTION PLAN TAB ==============
const ProductionPlanTab = () => {
  const [view, setView] = useState('monthly');
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setData(null); // Reset data when fetching to avoid stale data type issues
    try {
      const res = await planningAPI.getProductionPlan(view, month);
      setData(res);
    } catch { toast.error('Failed to load plan'); }
    setLoading(false);
  }, [view, month]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  return (
    <div className="space-y-4 mt-2" data-testid="plan-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {['monthly', 'weekly', 'daily'].map(v => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === v ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : !data ? (
        <div className="text-center py-16 text-gray-400 text-sm">No data</div>
      ) : view === 'monthly' ? (
        <MonthlyPlanView data={data} />
      ) : view === 'weekly' ? (
        <WeeklyPlanView data={data} />
      ) : (
        <DailyPlanView data={data} />
      )}
    </div>
  );
};

const MonthlyPlanView = ({ data }) => {
  const months = data.months || [];
  const plan = data.plan || {};

  return (
    <div className="space-y-4">
      {months.map(m => {
        const items = plan[m] || [];
        return (
          <Card key={m}>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#24549c]" /> {formatMonth(m)}
                <Badge variant="secondary" className="ml-auto text-[10px]">{items.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {items.length === 0 ? (
                <p className="text-center py-6 text-gray-400 text-xs">No forecasted items</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Forecast</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">In Progress</TableHead>
                      <TableHead className="text-right">Completed</TableHead>
                      <TableHead className="text-right">Gap</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it, i) => (
                      <TableRow key={i} className="text-xs" data-testid={`monthly-item-${i}`}>
                        <TableCell className="font-medium">{it.product_name}</TableCell>
                        <TableCell className="text-right">{it.forecast_qty.toLocaleString()} {it.unit}</TableCell>
                        <TableCell className="text-right text-blue-600">{it.production_planned}</TableCell>
                        <TableCell className="text-right text-amber-600">{it.production_in_progress}</TableCell>
                        <TableCell className="text-right text-emerald-600">{it.production_completed}</TableCell>
                        <TableCell className="text-right">
                          {it.gap > 0 ? <span className="text-red-600 font-semibold">{it.gap.toLocaleString()}</span> : <span className="text-emerald-600">0</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const WeeklyPlanView = ({ data }) => {
  const weeks = data.weeks || [];
  // Ensure plan is always an array (handle case when switching from monthly view where plan is an object)
  const plan = Array.isArray(data.plan) ? data.plan : [];

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold">{formatMonth(data.month)} — Weekly Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {plan.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-xs">No forecast data for weekly breakdown</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Monthly Total</TableHead>
                {weeks.map(w => <TableHead key={w.week} className="text-right">W{w.week}<br/><span className="text-[10px] text-gray-400">{w.start?.slice(5) || ''}</span></TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.map((p, i) => (
                <TableRow key={i} className="text-xs">
                  <TableCell className="font-medium">{p.product_name || '-'}</TableCell>
                  <TableCell className="text-right font-semibold">{(p.monthly_total ?? 0).toLocaleString()} {p.unit || ''}</TableCell>
                  {(p.weeks || []).map((w, j) => (
                    <TableCell key={j} className="text-right">{(w.target ?? 0).toLocaleString()}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const DailyPlanView = ({ data }) => {
  // Ensure plan is always an array (handle case when switching from monthly view where plan is an object)
  const plan = Array.isArray(data.plan) ? data.plan : [];
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          Today's Plan — {data.date || 'Loading...'}
          <Badge variant="secondary" className="text-[10px] ml-auto">{data.month || ''}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {plan.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-xs">No daily targets</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Monthly Target</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-right">Days Left</TableHead>
                <TableHead className="text-right">Adj. Daily</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.map((p, i) => (
                <TableRow key={i} className="text-xs" data-testid={`daily-item-${i}`}>
                  <TableCell className="font-medium">{p.product_name || '-'}</TableCell>
                  <TableCell className="text-right">{(p.monthly_target ?? 0).toLocaleString()} {p.unit || ''}</TableCell>
                  <TableCell className="text-right text-emerald-600">{p.completed_so_far ?? 0}</TableCell>
                  <TableCell className="text-right text-amber-600">{(p.remaining_target ?? 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">{p.remaining_days ?? 0}</TableCell>
                  <TableCell className="text-right font-bold text-[#24549c]">{(p.adjusted_daily ?? 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// ============== OC SHORTFALL TAB ==============
const ShortfallTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    planningAPI.getOCShortfall()
      .then(setData)
      .catch(() => toast.error('Failed to load shortfall data'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4 mt-2" data-testid="shortfall-tab">
      <div className="flex items-center gap-3">
        <Badge variant="outline" className="text-xs">
          <AlertTriangle className="w-3 h-3 mr-1 text-amber-500" />
          {data?.total_shortfall_ocs || 0} OCs with undelivered items
        </Badge>
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : !data?.shortfall?.length ? (
            <div className="text-center py-16 text-gray-400 text-sm">All OCs fully delivered</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>OC #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Dispatched</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead>Production Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.shortfall.map((s, i) => (
                  <TableRow key={i} className="text-xs" data-testid={`shortfall-row-${i}`}>
                    <TableCell className="font-medium font-mono text-[#24549c]">{s.oc_number || s.oc_id?.slice(0, 8)}</TableCell>
                    <TableCell>{s.customer_name}</TableCell>
                    <TableCell className="text-gray-500">{s.oc_date}</TableCell>
                    <TableCell className="text-right">{s.total_ordered}</TableCell>
                    <TableCell className="text-right text-emerald-600">{s.total_dispatched}</TableCell>
                    <TableCell className="text-right font-semibold text-red-600">{s.remaining}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {s.production_planned > 0 && <Badge variant="outline" className="text-[9px]">Planned: {s.production_planned}</Badge>}
                        {s.production_in_progress > 0 && <Badge className="text-[9px] bg-amber-100 text-amber-700">WIP: {s.production_in_progress}</Badge>}
                        {s.production_completed > 0 && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Done: {s.production_completed}</Badge>}
                        {s.production_planned === 0 && s.production_in_progress === 0 && s.production_completed === 0 && (
                          <span className="text-gray-400">Not started</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MRP TAB ==============
const MRPTab = () => {
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIndentDialog, setShowIndentDialog] = useState(false);
  const [indentLoading, setIndentLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [branches, setBranches] = useState([]);
  const [indentBranch, setIndentBranch] = useState('');
  const [indentPriority, setIndentPriority] = useState('Medium');
  
  // Get user role for authorization check
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const canGenerateIndent = ['Admin', 'Manager'].includes(user.role);

  const fetchMRP = useCallback(async () => {
    setLoading(true);
    try {
      const res = await planningAPI.getMaterialRequirements(month);
      setData(res);
    } catch { toast.error('Failed to load MRP'); }
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchMRP(); }, [fetchMRP]);
  useEffect(() => {
    branchAPI.getAll().then(b => setBranches(Array.isArray(b) ? b : [])).catch(() => {});
  }, []);

  const shortfallMaterials = data?.materials?.filter(m => m.net_indent_needed > 0) || [];

  const openIndentDialog = () => {
    setSelectedItems(new Set(shortfallMaterials.map(m => m.item_id)));
    setShowIndentDialog(true);
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleGenerateIndent = async () => {
    if (selectedItems.size === 0) { toast.error('Select at least one material'); return; }
    setIndentLoading(true);
    try {
      const res = await planningAPI.generateIndent({
        month,
        items: Array.from(selectedItems),
        branch: indentBranch,
        priority: indentPriority,
      });
      toast.success(res.message);
      setShowIndentDialog(false);
      fetchMRP(); // Refresh to show updated pending indents
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate indent');
    }
    setIndentLoading(false);
  };

  return (
    <div className="space-y-4 mt-2" data-testid="mrp-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        {canGenerateIndent && shortfallMaterials.length > 0 && (
          <Button size="sm" onClick={openIndentDialog} data-testid="generate-indent-btn" className="gap-1.5 bg-[#24549c] hover:bg-[#1a3f7a]">
            <FileDown className="w-3.5 h-3.5" /> Generate Indent ({shortfallMaterials.length})
          </Button>
        )}
        {data && (
          <div className="ml-auto flex gap-4 text-xs text-gray-500">
            <span>Materials: <strong className="text-gray-900">{data.total_materials}</strong></span>
            <span>Indent Value: <strong className="text-gray-900">Rs. {data.total_indent_value?.toLocaleString()}</strong></span>
          </div>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : !data?.materials?.length ? (
            <div className="text-center py-16 text-gray-400 text-sm">No material requirements. Add sales forecasts first.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>Material</TableHead>
                  <TableHead>Item ID</TableHead>
                  <TableHead className="text-right">Needed</TableHead>
                  <TableHead className="text-right">In Stock</TableHead>
                  <TableHead className="text-right">Pending Indent</TableHead>
                  <TableHead className="text-right">Net Indent</TableHead>
                  <TableHead className="text-right">Est. Cost</TableHead>
                  <TableHead>Used In</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.materials.map((m, i) => (
                  <TableRow key={i} className="text-xs" data-testid={`mrp-row-${i}`}>
                    <TableCell className="font-medium max-w-[200px] truncate">{m.item_description || m.item_id}</TableCell>
                    <TableCell className="text-gray-500 font-mono text-[10px]">{m.item_id}</TableCell>
                    <TableCell className="text-right">{m.total_needed?.toLocaleString()} {m.unit}</TableCell>
                    <TableCell className="text-right text-emerald-600">{m.in_stock?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-blue-600">{m.pending_indent?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {m.net_indent_needed > 0 ? <span className="text-red-600">{m.net_indent_needed.toLocaleString()}</span> : <span className="text-emerald-600">0</span>}
                    </TableCell>
                    <TableCell className="text-right">Rs. {m.estimated_cost?.toLocaleString()}</TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {(m.used_in || []).map((u, j) => <Badge key={j} variant="outline" className="text-[9px]">{u.product_name?.slice(0, 25)}</Badge>)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Indent Confirmation Dialog */}
      <Dialog open={showIndentDialog} onOpenChange={setShowIndentDialog}>
        <DialogContent className="max-w-2xl" data-testid="generate-indent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-4 h-4 text-[#24549c]" /> Generate Purchase Indent — {formatMonth(month)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Branch</label>
                <Select value={indentBranch} onValueChange={setIndentBranch}>
                  <SelectTrigger data-testid="indent-branch-select"><SelectValue placeholder="Select branch..." /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Priority</label>
                <Select value={indentPriority} onValueChange={setIndentPriority}>
                  <SelectTrigger data-testid="indent-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600">Materials to indent ({selectedItems.size}/{shortfallMaterials.length})</label>
                <div className="flex gap-2">
                  <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setSelectedItems(new Set(shortfallMaterials.map(m => m.item_id)))} data-testid="select-all-materials">Select All</button>
                  <button className="text-[10px] text-gray-400 hover:underline" onClick={() => setSelectedItems(new Set())} data-testid="deselect-all-materials">Deselect All</button>
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto border rounded-lg divide-y divide-gray-50">
                {shortfallMaterials.map((m, i) => (
                  <label key={i} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer" data-testid={`indent-item-${i}`}>
                    <input type="checkbox" checked={selectedItems.has(m.item_id)} onChange={() => toggleItem(m.item_id)}
                      className="rounded border-gray-300 text-[#24549c] focus:ring-[#24549c]" data-testid={`indent-checkbox-${i}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{m.item_description || m.item_id}</p>
                      <p className="text-[10px] text-gray-400 font-mono">{m.item_id}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-red-600">{m.net_indent_needed?.toLocaleString()} {m.unit}</p>
                      <p className="text-[10px] text-gray-400">Rs. {m.estimated_cost?.toLocaleString()}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIndentDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateIndent} disabled={indentLoading || selectedItems.size === 0}
              data-testid="confirm-generate-indent" className="bg-[#24549c] hover:bg-[#1a3f7a] gap-1.5">
              {indentLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckSquare className="w-3.5 h-3.5" />}
              Create Indent ({selectedItems.size} items)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== INDENT SUMMARY TAB ==============
const IndentSummaryTab = () => {
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await planningAPI.getIndentSummary(month);
      setData(res);
    } catch { toast.error('Failed to load indent summary'); }
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return (
    <div className="space-y-4 mt-2" data-testid="indent-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        {data && (
          <div className="ml-auto flex gap-4 text-xs text-gray-500">
            <span>System: <strong className="text-gray-900">{data.total_system_indent?.toLocaleString()}</strong></span>
            <span>Manual: <strong className="text-gray-900">{data.total_manual_indent?.toLocaleString()}</strong></span>
            <span>Combined: <strong className="text-[#24549c]">{data.total_combined?.toLocaleString()}</strong></span>
          </div>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : !data?.items?.length ? (
            <div className="text-center py-16 text-gray-400 text-sm">No indent data for {formatMonth(month)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Total Needed</TableHead>
                  <TableHead className="text-right">In Stock</TableHead>
                  <TableHead className="text-right">System Indent</TableHead>
                  <TableHead className="text-right">Manual Indent</TableHead>
                  <TableHead className="text-right">Total Indent</TableHead>
                  <TableHead className="text-right">Est. Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((m, i) => (
                  <TableRow key={i} className="text-xs" data-testid={`indent-row-${i}`}>
                    <TableCell className="font-medium max-w-[200px] truncate">{m.item_description || m.item_id}</TableCell>
                    <TableCell className="text-right">{m.total_needed?.toLocaleString()} {m.unit}</TableCell>
                    <TableCell className="text-right text-emerald-600">{m.in_stock?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-blue-600">{m.net_indent_needed?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-amber-600">{m.manual_indent_qty?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {m.total_indent > 0 ? <span className="text-red-600">{m.total_indent.toLocaleString()}</span> : '0'}
                    </TableCell>
                    <TableCell className="text-right">Rs. {m.estimated_cost?.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MONTH PICKER ==============
const MonthPicker = ({ value, onChange }) => {
  const shift = (dir) => {
    const [y, m] = value.split('-').map(Number);
    let nm = m + dir, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    onChange(`${ny}-${String(nm).padStart(2, '0')}`);
  };
  return (
    <div className="flex items-center gap-1 bg-white border rounded-lg px-1">
      <button onClick={() => shift(-1)} className="p-1.5 hover:bg-gray-50 rounded" data-testid="month-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
      <span className="text-xs font-medium min-w-[80px] text-center" data-testid="month-display">{formatMonth(value)}</span>
      <button onClick={() => shift(1)} className="p-1.5 hover:bg-gray-50 rounded" data-testid="month-next"><ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  );
};

export default PlanningModule;
